import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GoogleIcon from "@mui/icons-material/Google";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import EmailIcon from "@mui/icons-material/Email";
import KeyIcon from "@mui/icons-material/Key";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import { useAuthData } from "modules/Authentication/hooks/useAuthData";
import { ILoginFormType } from "types";

type Props = {};

export const LoginForm = (props: Props) => {
  const {
    onLogin,
    loading,
    error,
    userNotRegistered,
    loginWithGoogle,
    loginWithFacebook,
    loginWithTwitter,
  } = useAuthData();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === "email") {
      setEmail(value);
      validateEmail(value); // Validate email on each change
      setEmailError("");
    } else if (name === "password") {
      setPassword(value);
      validatePassword(value);
      setPasswordError("");
    }
  };

  const validateEmail = (value: string): void => {
    if (!value) {
      setEmailError("Email is required");
      setIsEmailValid(false);
      return;
    }

    // Basic email format validation using regex
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!regex.test(value)) {
      setEmailError("Invalid email format");
      setIsEmailValid(false);
      return;
    }

    setEmailError("");
    setIsEmailValid(true);
  };

  const validatePassword = (value?: string): void => {
    if (!value) {
      // If no value is provided, show "Please re-enter your password"
      setPasswordError("Please re-enter your password");
      return;
    }

    if (value.length < 8) {
      // If the password is less than 8 characters, show the appropriate error
      setPasswordError("Minimum 8 characters required");
      return;
    }

    // Add your custom logic to check password validity
    if (value !== "expectedPassword") {
      // Replace "expectedPassword" with your custom logic
      // setPasswordError("Please re-enter your password");
      return;
    }

    // If the password passes all checks, clear the error
    setPasswordError("");
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Validate inputs
    validateEmail(email);
    validatePassword(password);

    // If both inputs are valid, proceed with form submission logic
    if (isEmailValid && !passwordError) {
      const loginData: ILoginFormType = {
        email: email,
        password: password,
        isKeepLoggedIn: true, // Assuming you have logic for this
        isAdmin: false,
      };
      try {
        // Attempt to login
        const user = await onLogin(loginData);

        if (user) {
          // If login is successful, navigate to the dashboard
          navigate("/");
        }
      } catch (error: any) {
        // Catch unexpected errors
        if (error.message === "auth/user-not-found") {
          setPasswordError("User not found. Please check your email address.");
        } else {
          console.error("Login failed:", error);
        }
      }
    }
  };

  // social icons ///
  const handleSocialLogin = async (providerLoginFn: Function) => {
    try {
      const user = await providerLoginFn();
      console.log("handleSocialLogin", user);
      if (user) {
        navigate("/"); // Redirect on successful login
      } else {
        console.warn("⚠️ No user returned from social login");
      }
    } catch (error) {
      console.error("Social login failed", error);
    }
  };

  return (
    <React.Fragment>
      <form onSubmit={handleSubmit}>
        <Grid mt={3} container spacing={2} direction="column">
          <Grid item>
            <Box
              fontSize={{ xs: "20.76px", md: "28px" }}
              fontWeight={{ xs: "600", md: "500" }}
              color="black"
            >
              Login
            </Box>
            <Typography
              fontSize={{ xs: "15px", md: "18px" }}
              fontWeight={{ xs: "400", md: "400" }}
              variant="h6"
              color="#6D6D6D"
              display="flex"
              gap={"5px"}
            >
              Don’t have an account? Register
              <Box
                component={Link}
                to="/register"
                color="black"
                fontWeight={{ xs: "400", md: "400" }}
                sx={{ textDecoration: "none" }}
              >
                here
              </Box>
            </Typography>
          </Grid>

          <Grid item xs={12} mt={4}>
            <TextField
              type="email"
              name="email"
              variant="outlined"
              fullWidth
              placeholder="Enter your email"
              error={!!emailError}
              value={email}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon
                      sx={{
                        color: "#6D6D6D",
                        width: { xs: "11.86px", md: "20px" },
                        height: { xs: "12px", md: "20px" },
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {email && (
                      <Typography
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <Box
                          component="span"
                          sx={{ color: isEmailValid ? "#808080" : "red" }}
                        >
                          {isEmailValid ? (
                            <CheckIcon
                              sx={{
                                color: "green",
                                width: { xs: "11.86px", md: "20px" },
                                height: { xs: "12px", md: "20px" },
                              }}
                            />
                          ) : (
                            <CloseIcon
                              sx={{
                                color: "red",
                                width: { xs: "11.86px", md: "20px" },
                                height: { xs: "12px", md: "20px" },
                              }}
                            />
                          )}
                        </Box>
                      </Typography>
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: "white",
                borderRadius: "12px",
                "& .MuiInputBase-root": {
                  height: { xs: "35.39px", md: "48px" },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#6D6D6D",
                  fontSize: { xs: "12px", md: "16px" },
                  fontWeight: "500",
                  opacity: 1,
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderRadius: { xs: "8.9px", md: "12px" },
                    borderColor: emailError ? "red" : undefined,
                  },
                  "& input": {
                    color: emailError ? "red" : "inherit",
                    // height: '16px',
                  },
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              type={showPassword ? "text" : "password"}
              name="password"
              variant="outlined"
              fullWidth
              placeholder="Enter your password"
              error={!!passwordError}
              value={password}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      sx={{
                        width: { xs: "11.86px", md: "20px" },
                        height: { xs: "16px", md: "20px" }, // you can adjust height as needed
                        color: "#6D6D6D",
                      }}
                    >
                      <svg
                        style={{
                          width: "100%",
                          height: "100%",
                        }}
                        color="#6D6D6D"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          opacity="0.7"
                          d="M8.8125 10.75L7.75 11.7812C7.625 11.9375 7.4375 12 7.25 12H6V13.25C6 13.6875 5.65625 14 5.25 14H4V15.25C4 15.6875 3.65625 16 3.25 16H0.75C0.3125 16 0 15.6875 0 15.25V12.75C0 12.5625 0.0625 12.375 0.21875 12.2188L5.25 7.1875C5.0625 6.65625 5 6.09375 5 5.5C5 2.46875 7.4375 0 10.5 0C13.5312 0 16 2.46875 16 5.5C16 8.5625 13.5312 11 10.5 11C9.90625 11 9.34375 10.9375 8.8125 10.75ZM11.75 5.5C12.4375 5.5 13 4.96875 13 4.25C13 3.5625 12.4375 3 11.75 3C11.0312 3 10.5 3.5625 10.5 4.25C10.5 4.96875 11.0312 5.5 11.75 5.5Z"
                          fill="#6D6D6D"
                        />
                      </svg>
                    </Box>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {password && (
                      <Box component="span" sx={{ color: "red" }}>
                        {passwordError && (
                          <CloseIcon
                            sx={{
                              color: "red",
                              width: { xs: "11.86px", md: "20px" },
                              height: { xs: "12px", md: "20px" },
                            }}
                          />
                        )}
                      </Box>
                    )}
                    <Typography
                      onClick={togglePasswordVisibility}
                      sx={{ cursor: "pointer" }}
                    >
                      <Box component="span" sx={{ color: "#808080" }}>
                        {showPassword ? (
                          <VisibilityIcon
                            sx={{
                              width: { xs: "15px", md: "20px" },
                              height: { xs: "12px", md: "20px" },
                            }}
                          />
                        ) : (
                          <VisibilityOffIcon
                            sx={{
                              width: { xs: "15px", md: "20px" },
                              height: { xs: "12px", md: "20px" },
                            }}
                          />
                        )}
                      </Box>
                    </Typography>
                  </InputAdornment>
                ),
                sx: {
                  "& input": {
                    color: passwordError ? "red" : "inherit",
                  },
                },
              }}
              sx={{
                backgroundColor: "white",
                borderRadius: "12px",
                "& .MuiInputBase-root": {
                  height: { xs: "35.39px", md: "48px" },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#6D6D6D",
                  fontSize: { xs: "12px", md: "16px" },
                  fontWeight: "500",
                  opacity: 1,
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderRadius: { xs: "8.9px", md: "12px" },
                    borderColor: passwordError ? "red" : undefined,
                  },
                },
              }}
            />
            <Typography
              mt={emailError || passwordError ? 2 : 0}
              sx={{
                display: "flex",
                alignItems: "center",
                color: "red",
                fontSize: { xs: "10.38px", md: "14px" },
                fontWeight: "400",
              }}
            >
              {(emailError || passwordError) && (
                <ErrorIcon sx={{ width: "13px", height: "13px" }} />
              )}
              &nbsp;{emailError || passwordError}
              {/* Display error messages */}
              {(error || userNotRegistered) && (
                <Typography sx={{ color: "red" }}>
                  {error || userNotRegistered}
                </Typography>
              )}
            </Typography>
          </Grid>

          <Grid
            xs={12}
            ml={"15px"}
            mt={emailError || passwordError ? "0px" : "-13px"}
          >
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    "&.Mui-checked": {
                      color: "#2C2C2C",
                    },
                    "& .MuiSvgIcon-root": {
                      width: "19px",
                      height: "20px",
                    },
                  }}
                />
              }
              label="Remember me"
              sx={{
                "& .MuiFormControlLabel-label": {
                  color: "#6D6D6D",
                  fontSize: "16px",
                  ml: "-3px",
                  fontWeight: { xs: "400", md: "500" },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} mt={"-8px"} textAlign="right">
            <Link to="/reset-password" style={styles.link}>
              Forgot Password
            </Link>
          </Grid>

          <Grid item xs={12} mt={3}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              sx={{
                backgroundColor: "#233F91",
                borderRadius: { xs: "10px", md: "25px" },
                color: "white",
                height: "48px",
                "&:hover": {
                  backgroundColor: "#EEEEEE",
                  color: "#233F91",
                  border: "1.5px solid #233F91",
                },
              }}
            >
              Log In
            </Button>
          </Grid>
          <Grid
            item
            xs={12}
            mt={2}
            mb={5}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Typography>or</Typography>
            <Grid item mt={2} display="flex" gap={1} alignItems="center">
              <Box
                sx={styles.social}
                onClick={() => handleSocialLogin(loginWithGoogle)}
              >
                <img src="/assets/icons/google.svg" alt="google" />
              </Box>
              {/* <Box
                sx={styles.social}
                onClick={() => handleSocialLogin(loginWithFacebook)}
              >
                <img src="/assets/icons/facebook.svg" alt="facebook" />
              </Box>
              <Box
                sx={styles.social}
                onClick={() => handleSocialLogin(loginWithTwitter)}
              >
                <img src="/assets/icons/twitter.svg" alt="twitter" />
              </Box> */}
            </Grid>
          </Grid>

          {/* </Box> */}
        </Grid>
      </form>
    </React.Fragment>
  );
};

const styles = {
  link: {
    color: "#000000",
    lineHeight: "19.36px",
    textDecoration: "underline",
    fontWeight: "500",
    fontSize: "16px",
  },
  social: {
    display: "flex",
    p: "9px 20px 10px 20px",
    borderRadius: "8px",
    border: "1px solid #E1E1E1",
    boxShadow: 2,
    cursor: "pointer",
  },
};

export default LoginForm;
